import React from 'react';
import {
  BodyText,
  BodyTextStyle,
  CaptionStyle,
  Subtitle,
  Title,
  mq,
  resetButtonStyle
} from '@kvdbil/components';
import styled from 'styled-components';
import {
  LeasingPartnerPresentationFragment,
  LeasingPromotionCardFragment
} from '~/config/generated/graphql';
import { formatMoneyWithoutCurrency } from '~/helpers/currency';
import { useTranslation } from '~/Locale';
import PartnerLogo from './PartnerLogo';

const IMAGE_ASPECT_RATIO = '244 / 137';
const CARD_LOGO_HEIGHT = '1.5rem';

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.common.white};
  box-shadow: ${({ theme }) => theme.elevations.elevation2};
  color: ${({ theme }) => theme.colors.text.dark};
`;

const CardButton = styled.button`
  ${resetButtonStyle};
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 0.5rem;
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible::after {
    outline: 2px solid ${({ theme }) => theme.colors.info.mainAlt};
    outline-offset: -2px;
  }
`;

const Photo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background.lightGray};
`;

const ProductImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: ${IMAGE_ASPECT_RATIO};
  object-fit: cover;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  padding: 1rem;

  ${mq('tablet')} {
    gap: 0.5rem;
    padding: 1rem 1rem 0.5rem;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
`;

const CardLogo = styled(PartnerLogo)`
  height: ${CARD_LOGO_HEIGHT};
`;

const Label = styled(BodyText)`
  && {
    ${mq('tablet')} {
      ${CaptionStyle};
    }
  }
`;

const BrandAndModel = styled(Title)`
  && {
    ${mq('tablet')} {
      ${BodyTextStyle};
      font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
    }
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${({ theme }) => theme.colors.background.gray};
`;

const CostLabel = styled(Subtitle)`
  && {
    ${mq('tablet')} {
      ${BodyTextStyle};
    }
  }
`;

const CostValue = styled(Title)`
  && {
    text-align: right;

    ${mq('tablet')} {
      ${BodyTextStyle};
      font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
      text-align: right;
    }
  }
`;

const Terms = styled(BodyText)`
  && {
    ${mq('tablet')} {
      ${CaptionStyle};
    }
  }
`;

const TermsValue = styled(Terms)`
  && {
    text-align: right;
  }
`;

type Props = LeasingPromotionCardFragment & {
  logo?: LeasingPartnerPresentationFragment['logo'];
  onClick: (id: string) => void;
};

const LeasingPromotionCard = ({
  productImage,
  brandAndModel,
  monthlyCostFrom,
  leasingPeriodFrom,
  mileageFrom,
  logo,
  id,
  onClick
}: Props) => {
  const { t } = useTranslation();

  const image = productImage?.responsiveImage;

  if (monthlyCostFrom == null) {
    return null;
  }

  return (
    <Card>
      <Photo>
        {image && (
          <ProductImage
            src={image.src ?? undefined}
            srcSet={image.srcSet ?? undefined}
            sizes={image.sizes ?? undefined}
            alt={image.alt ?? brandAndModel ?? ''}
          />
        )}
      </Photo>

      <Content>
        <Row>
          <Label as="span">{t('Used car leasing')}</Label>
          <CardLogo logo={logo} />
        </Row>

        <BrandAndModel as="h4">
          <CardButton type="button" onClick={() => onClick(id)}>
            {brandAndModel}
          </CardButton>
        </BrandAndModel>

        <Divider />

        <div>
          <Row>
            <CostLabel as="span">{t('Cost')}</CostLabel>
            <CostValue as="span">
              {t(
                'fr. %s kr/month',
                formatMoneyWithoutCurrency(monthlyCostFrom)
              )}
            </CostValue>
          </Row>

          <Row>
            <Terms as="span">
              {leasingPeriodFrom != null &&
                t('fr. %s months', leasingPeriodFrom)}
            </Terms>
            <TermsValue as="span">
              {mileageFrom != null &&
                t('fr. %s mil/year', formatMoneyWithoutCurrency(mileageFrom))}
            </TermsValue>
          </Row>
        </div>
      </Content>
    </Card>
  );
};

export default LeasingPromotionCard;
